// src/components/communications/UnifiedThread.jsx
// Center panel - Unified message timeline with all communication types
import React, { useRef, useMemo, useEffect } from 'react';
import {
  MessageSquare, ChevronLeft, Loader2, Inbox, CheckCheck
} from 'lucide-react';
import ThreadItem from './ThreadItem';
import MessageComposer from './MessageComposer';

// Format date for header (dateStr is local YYYY-MM-DD from groupByDate)
const formatDateHeader = (dateStr) => {
  // Parse as local date (not UTC) by splitting components
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayMidnight = new Date(todayMidnight);
  yesterdayMidnight.setDate(yesterdayMidnight.getDate() - 1);
  const dateMidnight = new Date(year, month - 1, day);

  if (dateMidnight.getTime() === todayMidnight.getTime()) {
    return 'Today';
  }
  if (dateMidnight.getTime() === yesterdayMidnight.getTime()) {
    return 'Yesterday';
  }

  // Check if this week
  const weekAgo = new Date(todayMidnight);
  weekAgo.setDate(weekAgo.getDate() - 7);
  if (dateMidnight > weekAgo) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  // Check if this year
  if (year === today.getFullYear()) {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  }

  // Full date
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// Group messages by date (using local timezone, not UTC)
const groupByDate = (items) => {
  const groups = {};

  items.forEach(item => {
    const date = new Date(item.timestamp || item.createdAt);
    // Use local date components to avoid UTC shift
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(item);
  });

  return groups;
};

// Get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
};

export default function UnifiedThread({
  thread,
  client,
  loading,
  onSendMessage,
  composeMode,
  onComposeModeChange,
  playingId,
  audioLoading,
  onPlayAudio,
  isMobile,
  onBack,
  selectedClientId,
  hasUnread,
  onMarkRead,
  onLogOutcome
}) {
  const scrollRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive.
  // The container uses flex-col-reverse, so scrollTop=0 is the visual bottom (newest messages).
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [thread]);

  // Group thread items by date
  const groupedThread = useMemo(() => {
    return groupByDate(thread);
  }, [thread]);

  // Sorted date keys (oldest first — like a phone: scroll down for newest)
  const sortedDates = useMemo(() => {
    return Object.keys(groupedThread).sort((a, b) => new Date(a) - new Date(b));
  }, [groupedThread]);

  // Client name for display
  const clientName = client
    ? `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Client'
    : 'Client';
  const clientInitials = getInitials(clientName);

  // No client selected state
  if (!selectedClientId) {
    return (
      <div className="flex-1 flex flex-col bg-slate-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-8">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Select a conversation
            </h3>
            <p className="text-sm text-gray-500 max-w-xs">
              Choose a client from the list to view their communication history
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-slate-50 min-w-0">
      {/* Thread Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        {/* Back button (mobile) */}
        {isMobile && (
          <button
            onClick={onBack}
            className="p-1 -ml-1 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="w-6 h-6 text-gray-500" />
          </button>
        )}

        {/* Client Avatar */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {clientInitials}
        </div>

        {/* Client Info */}
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-gray-900 truncate">{clientName}</h2>
          {client?.phone && (
            <p className="text-xs text-gray-500">{client.phone}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {hasUnread && onMarkRead && (
            <button
              onClick={onMarkRead}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark Read
            </button>
          )}
          <span className="text-xs text-gray-400">
            {thread.length} message{thread.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Thread Content - flex-col-reverse starts scrolled to bottom like a phone */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto flex flex-col-reverse px-4 py-4"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500">Loading conversation...</p>
            </div>
          </div>
        ) : thread.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-600 mb-1">
                No messages yet
              </h3>
              <p className="text-xs text-gray-400">
                Start the conversation by sending a message below
              </p>
            </div>
          </div>
        ) : (
          <div>
            {sortedDates.map((dateKey) => (
              <div key={dateKey}>
                {/* Date Separator */}
                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs font-medium text-gray-400 px-3 py-1 bg-gray-100 rounded-full">
                    {formatDateHeader(dateKey)}
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                {/* Messages for this date */}
                {groupedThread[dateKey].map((item) => (
                  <ThreadItem
                    key={item.id}
                    item={item}
                    playingId={playingId}
                    audioLoading={audioLoading}
                    onPlayAudio={onPlayAudio}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Message Composer */}
      <MessageComposer
        clientId={selectedClientId}
        clientName={clientName}
        clientPhone={client?.phone}
        clientEmail={client?.email}
        mode={composeMode}
        onModeChange={onComposeModeChange}
        onSend={onSendMessage}
        onLogOutcome={onLogOutcome}
        disabled={!selectedClientId || loading}
      />
    </div>
  );
}
